import React, { useCallback, useEffect, useState } from 'react';
import { fetchAllItems } from '../../../requests/requests';
import { HierarchyApiError } from '../../../../api/hierarchy-client';
import {
  IsoDate,
  OccupancyRelationship,
  UnitKind,
  UnitOccupancyState,
  UnitStatus,
} from '../../../../interfaces/hierarchy-interfaces';
import {
  Pill,
  PillTone,
  Segmented,
  SegmentedIndicator,
  SegmentedLabel,
  SegmentedTab,
} from './dashboard-units.styled';
import { IndicatorGroup } from '../../../../motion';

/**
 * Shared vocabulary for the Units / Occupancy / Roster screens: the label maps
 * that turn DB enum values into product language, date helpers that do NOT
 * shift across timezones, and the two hooks every screen here needs.
 */

/* ─────────────────────────────  Labels  ─────────────────────────── */

// Enum values are DB CHECK-constraint strings; none of them are fit to show a
// human. Keyed by the union so adding a kind upstream is a compile error here
// rather than a raw 'parking' leaking into the UI.
export const UNIT_KIND_LABELS: Record<UnitKind, string> = {
  residential: 'Residential',
  commercial: 'Commercial',
  common: 'Common area',
  parking: 'Parking',
  storage: 'Storage',
  unassigned: 'Needs assignment',
};

// 'archived' reads as "Deleted" (R-20). The wire value is untouched — it is a
// column value, not copy — but to a user this state IS deletion: it is what a
// Delete produces on a unit whose occupancy history the FK will not let go of.
// Calling it "Archived" on a detail page while every control says Delete would
// reintroduce the second vocabulary this ruling removes.
export const UNIT_STATUS_LABELS: Record<UnitStatus, string> = {
  active: 'Active',
  offline: 'Offline',
  archived: 'Deleted',
};

export const RELATIONSHIP_LABELS: Record<OccupancyRelationship, string> = {
  resident: 'Resident',
  co_resident: 'Co-resident',
  occupant: 'Occupant',
  guarantor: 'Guarantor',
  authorized: 'Authorized',
};

export const unitKindLabel = (kind: UnitKind): string =>
  UNIT_KIND_LABELS[kind] ?? kind;

export const relationshipLabel = (value: OccupancyRelationship): string =>
  RELATIONSHIP_LABELS[value] ?? value;

/* ──────────────────────────  Formatting  ────────────────────────── */

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

// DATEONLY values arrive as 'YYYY-MM-DD' with no timezone. `new Date(s)` reads
// that as UTC midnight, which renders as the PREVIOUS DAY for anyone west of
// Greenwich — a move-in dated the 1st showing as the 31st. Splitting the string
// and building a local date keeps the calendar day the server meant.
export const formatDate = (value: IsoDate | null | undefined): string => {
  if (!value) {
    return '—';
  }

  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);

  if (!match) {
    return value;
  }

  const [, year, month, day] = match;
  const monthLabel = MONTHS[Number(month) - 1] ?? month;

  return `${Number(day)} ${monthLabel} ${year}`;
};

// Same reasoning in reverse: toISOString() converts to UTC first, so late in
// the evening it hands back tomorrow's date as the default for a move-in.
export const todayIso = (): IsoDate => {
  const now = new Date();
  const month = `${now.getMonth() + 1}`.padStart(2, '0');
  const day = `${now.getDate()}`.padStart(2, '0');

  return `${now.getFullYear()}-${month}-${day}`;
};

// bathrooms is numeric(3,1) and node-postgres returns numeric as a STRING to
// protect precision, so '1.5' arrives, not 1.5. Trim the trailing '.0' for
// display without ever doing float maths on it.
export const formatBathrooms = (value: string | null): string => {
  if (value === null || value === '') {
    return '—';
  }

  return value.replace(/\.0+$/, '');
};

/* ────────────────────────────  Area units  ──────────────────────── */

/**
 * Unit size is STORED as `units.sq_ft` — an integer, CHECK (sq_ft > 0) — and
 * that never changes. Metric is a DISPLAY preference converted at the edges, so
 * switching it does not rewrite a single row and two people looking at the same
 * building in different units are looking at the same number.
 *
 * Feet are the default because the column and every existing row are feet.
 */
export type MeasurementSystem = 'imperial' | 'metric';

const SQ_FT_PER_SQ_M = 10.763910416709722;

export const AREA_UNIT_LABEL: Record<MeasurementSystem, string> = {
  imperial: 'sq ft',
  metric: 'm²',
};

export const formatArea = (
  sqFt: number | null,
  system: MeasurementSystem
): string => {
  if (sqFt === null) {
    return '—';
  }

  if (system === 'imperial') {
    return `${sqFt.toLocaleString()} sq ft`;
  }

  // One decimal is the resolution a floor plan is quoted at. Showing more would
  // imply a precision the stored integer never had — 850 sq ft is not
  // 78.9676 m², it is "about 79".
  const sqM = sqFt / SQ_FT_PER_SQ_M;

  return `${sqM.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  })} m²`;
};

// What the user typed -> the integer the column takes. Floored at 1 because
// chk_unit_sqft is `sq_ft > 0`: a metric value small enough to round to 0 would
// otherwise reach Postgres and come back as an opaque constraint violation.
export const areaToSqFt = (value: number, system: MeasurementSystem): number =>
  Math.max(
    1,
    Math.round(system === 'imperial' ? value : value * SQ_FT_PER_SQ_M)
  );

// The inverse, for seeding an edit form's input. Returns a bare string with no
// thousands separators — it goes into <input type="number">, which rejects
// "1,200" and would silently render an empty box.
export const sqFtToAreaInput = (
  sqFt: number,
  system: MeasurementSystem
): string =>
  system === 'imperial'
    ? `${sqFt}`
    : `${Math.round((sqFt / SQ_FT_PER_SQ_M) * 10) / 10}`;

/* ───────────────────  Measurement-system preference  ─────────────── */

const SYSTEM_KEY = 'lobbero.units.measurementSystem';

const readStored = (): MeasurementSystem => {
  try {
    return window.localStorage.getItem(SYSTEM_KEY) === 'metric'
      ? 'metric'
      : 'imperial';
  } catch {
    // Private browsing / blocked storage. Feet for this session.
    return 'imperial';
  }
};

// Module-level rather than one useState per screen. The units list renders the
// toggle and the Size column together, but the unit form is a sibling modal and
// the detail screen carries its own toggle — independent copies of the
// preference would disagree the moment two of them were mounted at once.
let current: MeasurementSystem | null = null;
const listeners = new Set<(value: MeasurementSystem) => void>();

// Non-reactive read, for a component that must FREEZE the system it is working
// in rather than follow changes. The unit form uses this: re-interpreting a
// half-typed number as a different unit mid-edit would silently change what the
// user is about to save.
export const readMeasurementSystem = (): MeasurementSystem => {
  if (current === null) {
    current = readStored();
  }

  return current;
};

export const useMeasurementSystem = (): [
  MeasurementSystem,
  (value: MeasurementSystem) => void
] => {
  const [system, setSystem] = useState<MeasurementSystem>(
    readMeasurementSystem
  );

  useEffect(() => {
    listeners.add(setSystem);

    // A toggle elsewhere may have fired between the initial read and this
    // subscription; resync so a late mount cannot be left showing the old unit.
    setSystem(readMeasurementSystem());

    return () => {
      listeners.delete(setSystem);
    };
  }, []);

  const select = useCallback((value: MeasurementSystem) => {
    current = value;
    try {
      window.localStorage.setItem(SYSTEM_KEY, value);
    } catch {
      // The switch still holds for this session.
    }
    listeners.forEach(listener => listener(value));
  }, []);

  return [system, select];
};

const SYSTEMS: MeasurementSystem[] = ['imperial', 'metric'];

// Two explicit segments rather than one flipping button: a single control
// labelled "m²" is ambiguous about whether that is the current state or the
// thing it will switch you to.
export const AreaUnitToggle: React.FC<{
  system: MeasurementSystem;
  onChange: (value: MeasurementSystem) => void;
}> = ({ system, onChange }) => (
  <Segmented role="group" aria-label="Show sizes in">
    {/* The selected pill is ONE element that slides to the pressed segment.
        aria-pressed stays: this is a two-state toggle pair, not a tab list. */}
    <IndicatorGroup id="area-unit">
      {SYSTEMS.map(value => (
        <SegmentedTab
          key={value}
          type="button"
          active={system === value}
          aria-pressed={system === value}
          onClick={() => onChange(value)}
        >
          {system === value && <SegmentedIndicator id="area-unit" />}
          <SegmentedLabel>{AREA_UNIT_LABEL[value]}</SegmentedLabel>
        </SegmentedTab>
      ))}
    </IndicatorGroup>
  </Segmented>
);

export const occupantFullName = (occupant: {
  first_name: string;
  last_name: string;
}): string => {
  const name = `${occupant.first_name ?? ''} ${
    occupant.last_name ?? ''
  }`.trim();

  return name || 'Unnamed occupant';
};

// Every screen here talks to hierarchy-client, which normalizes failures into
// HierarchyApiError with the SERVER's wording already in `.message`. Anything
// else is a genuine surprise and gets a generic line rather than [object
// Object].
export const readError = (error: unknown, fallback: string): string => {
  if (error instanceof HierarchyApiError) {
    return error.message;
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallback;
};

/* ──────────────────────────  Status pills  ──────────────────────── */

// A unit whose kind is still 'unassigned' is a migration placeholder standing
// in for a resident who had no unit number. That is an ACTION ITEM, not a
// failure, so it reads as warning-tinted — never alert.
export const KindPill: React.FC<{ kind: UnitKind }> = ({ kind }) => {
  const tone: PillTone = kind === 'unassigned' ? 'warning' : 'neutral';

  return <Pill tone={tone}>{unitKindLabel(kind)}</Pill>;
};

// Vacancy is derived server-side from open occupancies, so this only renders
// what it is told — it never recomputes "is anyone here" from the occupant list.
export const OccupancyPill: React.FC<{ state: UnitOccupancyState }> = ({
  state,
}) => {
  if (state.is_vacant) {
    return <Pill tone="neutral">Vacant</Pill>;
  }

  const suffix =
    state.occupant_count === 1
      ? '1 occupant'
      : `${state.occupant_count} occupants`;

  return <Pill tone="success">{`Occupied · ${suffix}`}</Pill>;
};

/* ────────────────────────────  Buildings  ───────────────────────── */

export interface BuildingOption {
  id: number;
  name: string;
  address: string;
  // Which portfolio holds this building, so a picker can group its options and
  // an owner with buildings across several portfolios can find the right one.
  //
  // null for a caller without portfolio:read — /dashboard/buildings is gated on
  // building:read and the server deliberately omits portfolio_name for a
  // concierge, who is not a portfolio audience. groupByPortfolio() falls back to
  // a flat list in that case.
  portfolioName: string | null;
}

// The units API keys off properties.id (INTEGER), while the legacy
// /dashboard/buildings payload types id as a string. Coerce once, here, so no
// screen builds a URL out of a string id.
interface RawBuilding {
  id: number | string;
  property_name?: string;
  address?: string;
  portfolio_name?: string | null;
}

// Group building options by their portfolio for an <optgroup> picker.
//
// Returns null when grouping would add nothing — no portfolio names came back,
// or every building sits in the same portfolio — so the caller renders its
// existing flat <option> list rather than one pointless group wrapper.
export const groupByPortfolio = (
  buildings: BuildingOption[]
): Array<{ portfolio: string; buildings: BuildingOption[] }> | null => {
  const named = buildings.filter(b => b.portfolioName);

  if (named.length === 0) {
    return null;
  }

  const groups = new Map<string, BuildingOption[]>();

  buildings.forEach(building => {
    // A building whose portfolio the caller cannot see still has to appear.
    const key = building.portfolioName || 'Other buildings';
    const bucket = groups.get(key);

    if (bucket) {
      bucket.push(building);
    } else {
      groups.set(key, [building]);
    }
  });

  if (groups.size < 2) {
    return null;
  }

  return [...groups.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([portfolio, rows]) => ({ portfolio, buildings: rows }));
};

export interface BuildingsState {
  buildings: BuildingOption[];
  loading: boolean;
  error: string;
}

// `enabled` exists for the same reason it does on useOrgUsers below: a caller
// that is NOT offering a building picker (because a host screen already owns the
// choice, or because the user cannot see the screen at all) should not fire a
// request whose result it will throw away.
export const useBuildings = (enabled = true): BuildingsState => {
  const [buildings, setBuildings] = useState<BuildingOption[]>([]);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!enabled) {
      setLoading(false);

      return;
    }

    let cancelled = false;

    fetchAllItems('dashboard/buildings')
      .then(data => {
        if (cancelled) {
          return;
        }

        const rows = Array.isArray(data) ? (data as RawBuilding[]) : [];

        setBuildings(
          rows.map(row => ({
            id: Number(row.id),
            name: row.property_name || `Building ${row.id}`,
            address: row.address || '',
            portfolioName: row.portfolio_name || null,
          }))
        );
      })
      .catch(() => {
        if (!cancelled) {
          setError('Could not load your buildings.');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return { buildings, loading, error };
};

/* ──────────────────────────  Org users  ─────────────────────────── */

export interface OrgUser {
  id: string;
  name: string;
  email: string;
  role: string;
}

interface RawUser {
  id: string;
  full_name?: string;
  email?: string;
  role?: string;
}

export interface OrgUsersState {
  users: OrgUser[];
  loading: boolean;
  // Set when the list could not be read at all. The move-in form treats this as
  // "offer name-only occupants and say why", NOT as a hard failure: linking an
  // account is optional, so losing the picker must not block a move-in.
  unavailable: boolean;
}

// /dashboard/users is gated on user:read, which a concierge does not hold. The
// caller passes `enabled: false` when the picker is not being offered, so the
// screen never fires a request it knows will 403.
export const useOrgUsers = (enabled: boolean): OrgUsersState => {
  const [users, setUsers] = useState<OrgUser[]>([]);
  const [loading, setLoading] = useState(enabled);
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);

      return;
    }

    let cancelled = false;

    setLoading(true);
    fetchAllItems('dashboard/users')
      .then(data => {
        if (cancelled) {
          return;
        }

        const rows = Array.isArray(data) ? (data as RawUser[]) : [];

        setUsers(
          rows
            .filter(row => !!row.id)
            .map(row => ({
              id: row.id,
              name: (row.full_name || '').trim(),
              email: (row.email || '').trim(),
              role: row.role || '',
            }))
            .sort((a, b) =>
              (a.name || a.email).localeCompare(b.name || b.email)
            )
        );
      })
      .catch(() => {
        if (!cancelled) {
          setUnavailable(true);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return { users, loading, unavailable };
};

// users.full_name is one encrypted column, but occupancies stores first_name /
// last_name as separate PLAINTEXT fields the front desk searches. Sending the
// split alongside user_id guarantees the roster has a readable name even if the
// server-side decrypt of the profile comes back empty.
export const splitName = (
  fullName: string
): { first_name: string; last_name: string } => {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);

  if (parts.length === 0) {
    return { first_name: '', last_name: '' };
  }

  if (parts.length === 1) {
    return { first_name: parts[0], last_name: '' };
  }

  return {
    first_name: parts[0],
    last_name: parts.slice(1).join(' '),
  };
};

/* ─────────────────────  Selection persistence  ──────────────────── */

// Which building the user last looked at, remembered across the Units ->
// Detail -> back trip and across the Units / Roster screens. Front desk staff
// work one building all day; re-picking it on every visit is the single most
// irritating thing these screens could do.
const BUILDING_KEY = 'lobbero.units.buildingId';

export const useRememberedBuilding = (): [
  number | null,
  (id: number | null) => void
] => {
  const [buildingId, setBuildingId] = useState<number | null>(() => {
    try {
      const stored = window.localStorage.getItem(BUILDING_KEY);
      const parsed = stored ? Number(stored) : NaN;

      return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
    } catch {
      return null;
    }
  });

  const select = useCallback((id: number | null) => {
    setBuildingId(id);
    try {
      if (id === null) {
        window.localStorage.removeItem(BUILDING_KEY);
      } else {
        window.localStorage.setItem(BUILDING_KEY, String(id));
      }
    } catch {
      // Private-browsing / quota. The selection still works for this session.
    }
  }, []);

  return [buildingId, select];
};
